import { ComplexValue, LoadOptions, Value } from 'app/types-shared';
import { MeatballPanelConfiguration, ValidationProps } from 'ui';
import { SearchFiltersTranslation } from '../constants';
import { FilterCommonTypes } from '../types-store';

export enum PredefinedIds {
  filter = 'predefined_filter',
  include = 'predefined_include',
  exclude = 'predefined_exclude'
}

export enum PredefinedFilterType {
  code = 'code',
  includeExclude = 'includeExclude'
}

export enum CustomFilterType {
  code = 'code',
  codeWithoutExclude = 'codeWithoutExclude',
  codeHierarchy = 'codeHierarchy',
  codeHierarchySubLevelWithCategory = 'codeHierarchySubLevelWithCategory',
  codeHierarchySubLevelWithCategoryHierarchical = 'codeHierarchySubLevelWithCategoryHierarchical',
  codeHierarchySubLevel = 'codeHierarchySubLevel',
  codeHierarchyParents = 'codeHierarchyParents',
  checkbox = 'checkbox',
  inputIntegerFilter = 'inputIntegerFilter',
  inputIntegerWithoutExclude = 'inputIntegerWithoutExclude',
  date = 'date',
  dateRange = 'dateRange',
  textSpecified = 'textSpecified',
  dateSpecificPeriod = 'dateSpecificPeriod',
  text = 'text',
  decimalRange = 'decimalRange',
  defaultValueSingleHierarchy = 'defaultValueSingleHierarchy',
  defaultValueSingleHierarchyParents = 'defaultValueSingleHierarchyParents',
  defaultValueMultiple = 'defaultValueMultiple',
  defaultValueMultipleSubLevel = 'defaultValueMultipleSubLevel',
  externalValueCheckbox = 'externalValueCheckbox',
  externalValue = 'externalValue'
}

export interface PredefinedFilter {
  type: PredefinedFilterType;
  values: ComplexValue<number | string>[];
}

export interface FilterConfiguration<T> {
  component: React.FC<FilterCommonProps<T>> | React.ComponentClass<FilterCommonProps<T>>;
  menuOptions: FilterMenuOption;
  locale?: string;
}

export interface FilterMenuOption {
  clear?: boolean;
  excludeInclude?: boolean;
  excludeIncludeChildren?: boolean;
  excludeIncludeParents?: boolean;
}

export interface FilterMenuValues {
  exclude?: boolean;
  excludeChildren?: boolean;
  excludeParents?: boolean;
}

export interface DefaultValueParams {
  codetableId?: string;
  caseTypeId?: string;
  cleanValues?: string[];
  readOnly?: boolean;
}

export interface ExternalValueParam {
  importSet: number;
  valueOperator: Value;
}
export interface ExternalValueParams {
  queryWithoutMatchExact: ExternalValueParam;
  queryWithMatchExact: ExternalValueParam;
  notSpecified?: ExternalValueParam;
}

interface FilterValues {
  optionsEndpoint?: string;
  optionsParams?: any;
  mapOptionsParams?: (payload: any) => object;
  mapOptionsRequestPayload?: (payload: any) => object;
  categoryOptionsEndpoint?: string;
  categoryOptionsParams?: any;
  categoryOptions?: ComplexValue<number | string>[];
  fetchingCategoryOptions?: boolean;
  categoryLoadOptionsParams?: LoadOptions;
  isHierarchy?: boolean;
  defaultValueParams?: DefaultValueParams;
  loadOptionsParams?: LoadOptions;
  includeDisabledLabel?: string;
  externalValueParams?: ExternalValueParams;
  hideDefaultFilterStyles?: boolean;
  allowQuerySearch?: boolean;
}

export interface FilterField extends FilterValues {
  fieldTypeId: string;
  type: CustomFilterType;
  label?: string;
  options?: ComplexValue<number>[];
  divider?: boolean;
  includeInSummary?: boolean;
  negated?: boolean;
}

export interface Filter {
  id: string;
  label: string;
  type: FilterType;
  group?: string;
  fields: FilterField[];
  options?: FilterOptions;
}

export interface FilterOptions {
  dateToAsEndOfDay?: boolean;
}

export enum FilterType {
  TEXT,
  TEXT_RANGE,
  DATE_RANGE,
  DECIMAL_RANGE,
  SELECTED_IDS,
  DEFAULT_VALUE,
  CUSTOM,
  EXTERNAL_VALUE,
  SPECIFIED_TEXT
}

export interface FilterValidation {
  id: string;
  validation: ValidationProps;
}

export interface FilterCommonValues<T> {
  value: T;
  description?: string;
  title?: string;
  optionType?: OptionType;
  subOptionType?: SubOptionType;
}

export interface FilterDefaultValues<T> {
  codetableId: FilterCommonValues<T>[];
  caseTypeId?: FilterCommonValues<T>[];
  cleanValues?: string[];
  readOnly?: boolean;
}

export type FilterPayloadValues<T> = { [key: string]: FilterCommonValues<T>[] };

export interface FilterCommonProps<T> extends FilterValues {
  id: string;
  label?: string;
  values: FilterCommonValues<T>[];
  defaultValue?: FilterDefaultValues<T>;
  externalValue?: ExternalValueParams;
  options?: ComplexValue<number | string>[];
  fetchingOptions: boolean;
  translation: SearchFiltersTranslation;
  locale: string;
  validation?: ValidationProps;
  filterValidation: FilterValidation[];
  onChange: (id: string, values: FilterCommonValues<T>[]) => void;
  onLoadOptions: (id: string, optionsEndpoint: string, loadOptionsParams: any, optionsParams?: any, optionsRequestPayload?: any) => void;
  updateFilterValues: (id: string, values: FilterCommonValues<FilterCommonTypes>[]) => void;
  onFilterValidationChange: (filterValidation: FilterValidation) => void;
  meatballPanelConfiguration?: MeatballPanelConfiguration;
}

export interface FilterOption {
  codes?: ComplexValue<number | string>[];
  query?: string;
  fetching: boolean;
}

/**
 * Filter external props
 */
export interface FilterConfigurationProps {

  filters: Filter[];

  values: FilterPayloadValues<FilterCommonTypes>;

  defaultFilterValues: FilterPayloadValues<FilterCommonTypes>;

  onChange: (id: string, values: FilterCommonValues<FilterCommonTypes>[]) => void;

  menuValues: { [key: string]: FilterMenuValues };
  onChangeMenu: (id: string, values: FilterMenuValues) => void;

  filterValidation: FilterValidation[];
  onFilterValidationChange: (filterValidation: FilterValidation) => void;

  options: { [key: string]: FilterOption };
  onLoadOptions: (id: string, optionsEndpoint: string, loadOptionsParams: any, optionsParams?: any) => void;
  updateFilterValues: (id: string, values: FilterCommonValues<FilterCommonTypes>[]) => void;

  predefinedFilters: PredefinedFilter;

  openFilters: { [key: string]: boolean };
  openCloseFilter: (id: string, open: boolean) => void;

  clearAllFilters: () => void;
}

export interface MapToSummaryPayload {
  filters: Filter[];
  values: FilterPayloadValues<FilterCommonTypes>;
  menuValues: { [key: string]: FilterMenuValues };
  translation: SearchFiltersTranslation;
  locale: string;
  filterValidation: FilterValidation[];
}

export interface MapCustomFieldPayload extends Partial<MapToSummaryPayload> {
  id: string;
  label: string;
  filterType: FilterType;
  fieldType: CustomFilterType;
}

export enum OptionType {
  specified = 'specified',
  notSpecified = 'notSpecified'
}

export enum SubOptionType {
  dateFrom = 'dateFrom',
  dateTo = 'dateTo',
  decimalFrom = 'decimalFrom',
  decimalTo = 'decimalTo',
  text = 'text',
  matchExact = 'matchExact'
}
